import { AdminPortal, useAuth, useLoginWithRedirect } from '@frontegg/react';
import { Button, Stack } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useAtom } from 'jotai';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { GenericFallbacksWrapper } from './components/Fallback/GenericFallbacksWrapper';
import { Loader } from './components/Loader/Loader';
import { ModalProvider } from './components/Modal/ModalContext';
import { ToastMessageProvider } from './components/ToastMessage/ToastMessageProvider';
import { useDebugKeyboard } from './debug/useDebugKeyboard';
import { HelpMessage } from './pages/CompanyProfiles/components/Messages/HelpMessage';
import { PermissionKey, PersonaKey } from './services/PermissionAndRolesKeys';
import { PermissionService } from './services/PermissionService';
import { authenticatedUserState } from './services/state/AuthState';
import { decodeJwtPayload } from './util/jwt';

const TOKEN_REFRESH_GUARD = 'fs_token_refresh';
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

export function isTokenExpired(accessToken: string): boolean {
  const exp = decodeJwtPayload(accessToken)?.exp;
  if (typeof exp !== 'number') {
    return true;
  }
  return exp * 1000 < Date.now() + TOKEN_EXPIRY_BUFFER_MS;
}

// Portal-only users manage accounts in Frontegg and have nothing to see in the
// app. Someone who also holds an app role keeps the app (MAGGIE-9123); the
// landing-page permissions decide that, not a count of roles.
function isPortalOnlyUser(): boolean {
  const permSvc = PermissionService.get();
  return (
    permSvc.hasPersona(PersonaKey.portalOnly) &&
    !permSvc.hasPermission(PermissionKey.canViewMetrics) &&
    !permSvc.hasPermission(PermissionKey.canViewList)
  );
}

export function AuthWrapperFrontEgg(props: PropsWithChildren) {
  const { user: frontEggUser, isAuthenticated, isLoading: authLoading, error: authError } = useAuth();
  const loginWithRedirect = useLoginWithRedirect();
  const [user, setUser] = useAtom(authenticatedUserState);
  const navigate = useNavigate();
  const fronteggSpanRef = useRef<Sentry.Span | undefined>(
    authLoading ? Sentry.startInactiveSpan({ name: 'app.init.frontegg', op: 'app.init' }) : undefined
  );

  useDebugKeyboard();

  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (fronteggSpanRef.current) {
      fronteggSpanRef.current.end();
      fronteggSpanRef.current = undefined;
    }

    if (isAuthenticated && frontEggUser) {
      if (frontEggUser.accessToken && isTokenExpired(frontEggUser.accessToken)) {
        if (!sessionStorage.getItem(TOKEN_REFRESH_GUARD)) {
          sessionStorage.setItem(TOKEN_REFRESH_GUARD, '1');
          localStorage.setItem('previousURL', `${window.location.pathname}${window.location.search}`);
          loginWithRedirect();
          return;
        }
        Sentry.captureMessage('Token still expired after redirect', {
          level: 'warning',
          tags: { feature: 'token-refresh-guard' },
        });
      } else {
        sessionStorage.removeItem(TOKEN_REFRESH_GUARD);
      }

      setUser(frontEggUser);
      PermissionService.initService(frontEggUser);

      if (isPortalOnlyUser()) {
        AdminPortal.show();
        return;
      }

      if (localStorage.getItem('previousURL')) {
        navigate(localStorage.getItem('previousURL')!);
        localStorage.removeItem('previousURL');
      }
    } else {
      setUser(undefined);
      localStorage.setItem('previousURL', `${window.location.pathname}${window.location.search}`);
      loginWithRedirect();
    }
  }, [frontEggUser, isAuthenticated, authLoading, loginWithRedirect, navigate, setUser]);

  if (authLoading || !user) {
    return <Loader />;
  }

  if (authError) {
    return <HelpMessage severity={'error'} message={'Authentication Error: ' + authError.message} />;
  }

  if (!PermissionService.isInitialized()) {
    return null;
  }
  if (isPortalOnlyUser()) {
    return (
      <Stack>
        <Button size='large' variant='contained' color='secondary' onClick={() => AdminPortal.show()}>
          Launch Admin Portal
        </Button>
      </Stack>
    );
  }

  return (
    <GenericFallbacksWrapper>
      <ToastMessageProvider>
        <ModalProvider>{props.children}</ModalProvider>
      </ToastMessageProvider>
    </GenericFallbacksWrapper>
  );
}
